import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { captureGclid, getGclid, getSource } from "@/utils/gclid";
import {
  Phone, WhatsappLogo, CheckCircle, Clock, ShieldCheck, MapPin, Star,
  ArrowRight, Wrench, Drop, Lightning, Gear, Warning, SealCheck,
  Timer, CurrencyInr, Medal, CaretDown, Headset, Package,
  Checks, SpinnerGap, Lock, Plugs
} from "@phosphor-icons/react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE = "9742119056";
const WA = `https://wa.me/91${PHONE}?text=Hi%2C%20I%20need%20washing%20machine%20repair%20in%20Bangalore`;

const BRANDS = ["LG", "Samsung", "IFB", "Bosch", "Whirlpool", "Haier", "Panasonic"];

const PROBLEMS = [
  { icon: SpinnerGap, title: "Not Spinning", desc: "Drum won't rotate or spin cycle fails" },
  { icon: Drop, title: "Water Leakage", desc: "Leaking from bottom, door, or hose" },
  { icon: Gear, title: "Drainage Issue", desc: "Water not draining after wash cycle" },
  { icon: Lightning, title: "PCB Issue", desc: "Electronic board malfunction or error codes" },
  { icon: Wrench, title: "Motor Failure", desc: "Motor not starting or making grinding noise" },
  { icon: Warning, title: "Drum Noise", desc: "Loud banging, rattling during operation" },
  { icon: Plugs, title: "Power Problem", desc: "Won't turn on or trips circuit breaker" },
  { icon: Lock, title: "Door Lock Issue", desc: "Door won't open or close properly" },
];

const PRICING = [
  { service: "Inspection & Diagnosis", price: "350", icon: CheckCircle, color: "from-blue-500 to-blue-600" },
  { service: "Drainage Issue Fix", price: "699", icon: Drop, color: "from-cyan-500 to-cyan-600" },
  { service: "PCB Board Repair", price: "1,699", icon: Lightning, color: "from-amber-500 to-amber-600" },
  { service: "Motor Repair", price: "2,250", icon: Gear, color: "from-orange-500 to-orange-600" },
];

const WHY_US = [
  { icon: Clock, title: "Same Day Service", desc: "Technician at your door within 60 minutes" },
  { icon: SealCheck, title: "Certified Technicians", desc: "Trained & background-verified experts" },
  { icon: Package, title: "Genuine Spare Parts", desc: "Original parts with manufacturer warranty" },
  { icon: ShieldCheck, title: "90-Day Warranty", desc: "Full warranty on all repairs done" },
  { icon: CurrencyInr, title: "Affordable Pricing", desc: "Transparent rates, no hidden charges" },
  { icon: Headset, title: "Doorstep Repair", desc: "We come to you. No need to carry machine" },
];

const REVIEWS = [
  { name: "Priya Sharma", area: "Whitefield", text: "Samsung front-load wasn't spinning at all. Technician came in 40 mins, diagnosed the belt issue, and fixed it on the spot. ₹700 total. Excellent service!", rating: 5 },
  { name: "Rahul Menon", area: "HSR Layout", text: "My IFB machine was leaking badly. They replaced the gasket with genuine parts and gave 90-day warranty. Way better than the brand service center.", rating: 5 },
  { name: "Sneha Patil", area: "Marathahalli", text: "PCB repair done perfectly on my LG machine. Other services quoted ₹4000+, these guys did it for ₹1700 with warranty. Highly recommended!", rating: 5 },
];

const AREAS = [
  "Whitefield", "Marathahalli", "HSR Layout", "Electronic City", "JP Nagar",
  "Indiranagar", "Yelahanka", "KR Puram", "Koramangala", "BTM Layout",
  "Banashankari", "Jayanagar", "Hebbal", "Sarjapur Road", "Bellandur"
];

const FAQS = [
  { q: "How quickly can a technician arrive?", a: "We aim for 30-60 minute response across Bangalore. Same-day service is guaranteed for bookings before 4 PM." },
  { q: "Do you provide warranty on repairs?", a: "Yes, all our repairs come with a 90-day warranty covering both parts and labour." },
  { q: "Which brands do you repair?", a: "We service all major brands — LG, Samsung, IFB, Bosch, Whirlpool, Haier, Panasonic, and more." },
  { q: "Is the inspection charge adjustable?", a: "Yes! The ₹350 inspection fee is adjusted against the final repair bill if you proceed with the repair." },
  { q: "Do you use genuine spare parts?", a: "Absolutely. We only use original manufacturer parts with proper warranty documentation." },
];

function Fade({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay }} className={className}>
      {children}
    </motion.div>
  );
}

// ─── HEADER ───
function WMHeader() {
  return (
    <header data-testid="wm-header" className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
        <span className="font-['Poppins'] font-bold text-[#0F172A] text-base uppercase tracking-tight">
          Home Tech Repair
        </span>
        <div className="flex items-center gap-2">
          <a href={`tel:${PHONE}`} data-testid="wm-header-call"
            className="hidden sm:flex bg-white text-[#0F172A] border border-slate-200 hover:border-[#FF6B00] font-semibold text-sm px-4 py-2.5 rounded-full items-center gap-1.5 transition-all font-['Poppins']">
            <Phone size={14} weight="fill" /> {PHONE}
          </a>
          <a href="#book" data-testid="wm-header-book"
            className="bg-[#FF6B00] text-white hover:bg-[#E55F00] font-bold text-sm px-5 py-2.5 rounded-full transition-all shadow-lg shadow-orange-200 flex items-center gap-1.5 font-['Poppins']">
            <ArrowRight size={16} weight="bold" /> Book Now
          </a>
        </div>
      </div>
    </header>
  );
}

// ─── HERO FORM (above the fold) ───
function HeroForm() {
  const [form, setForm] = useState({ name: "", phone: "", area: "", service: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const problems = ["Not Spinning", "Water Leakage", "Drainage Issue", "PCB Issue", "Motor Failure", "Drum Noise", "Power Problem", "Door Lock", "Other"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.area || !form.service) { toast.error("Please fill all fields"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, { ...form, gclid: getGclid(), source: getSource() });
      if (res.data.success) { navigate(`/thank-you?name=${encodeURIComponent(form.name)}&service=washing-machine`); }
    } catch { toast.error("Something went wrong. Please try again."); }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="wm-hero-form" className="bg-white p-6 sm:p-8 rounded-3xl shadow-2xl border border-slate-100">
      <h3 className="font-['Poppins'] text-xl font-bold text-[#0F172A] mb-1">Get Instant Repair</h3>
      <p className="text-xs text-slate-400 font-['Poppins'] mb-5">Fill the form — we'll reach you in 30 minutes</p>
      <div className="space-y-3">
        <input data-testid="wm-hero-form-name" type="text" placeholder="Your Name" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-sm font-['Poppins'] focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/10 outline-none transition-all" />
        <input data-testid="wm-hero-form-phone" type="tel" placeholder="Mobile Number" value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-sm font-['Poppins'] focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/10 outline-none transition-all" />
        <input data-testid="wm-hero-form-area" type="text" placeholder="Your Area in Bangalore" value={form.area}
          onChange={(e) => setForm({ ...form, area: e.target.value })}
          className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-sm font-['Poppins'] focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/10 outline-none transition-all" />
        <Select value={form.service} onValueChange={(val) => setForm({ ...form, service: val })}>
          <SelectTrigger data-testid="wm-hero-form-service" className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 h-auto text-sm font-['Poppins'] focus:ring-2 focus:ring-[#FF6B00]/10">
            <SelectValue placeholder="Select Problem" />
          </SelectTrigger>
          <SelectContent>
            {problems.map(p => <SelectItem key={p} value={p.toLowerCase().replace(/\s/g, "-")}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <button type="submit" disabled={loading} data-testid="wm-hero-form-submit"
        className="w-full mt-5 bg-[#FF6B00] text-white hover:bg-[#E55F00] font-bold text-base px-8 py-4 rounded-2xl shadow-xl shadow-orange-200 transition-all flex items-center justify-center gap-2 font-['Poppins'] disabled:opacity-50">
        {loading ? "Booking..." : "Book a Technician Now"} {!loading && <ArrowRight size={18} weight="bold" />}
      </button>
    </form>
  );
}

// ─── HERO ───
function WMHero() {
  return (
    <section data-testid="wm-hero" className="pt-16 bg-[#F8FAFC] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-[#FF6B00]/10 text-[#FF6B00] text-xs font-bold px-4 py-2 rounded-full mb-5 font-['Poppins']">
              <Checks size={16} weight="fill" /> Available Today &middot; Starting ₹350
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
              className="font-['Poppins'] text-4xl sm:text-5xl lg:text-[3.2rem] font-extrabold text-[#0F172A] leading-[1.1] tracking-tight mb-5">
              Bangalore's Trusted{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B00] to-[#FF8C00]">Washing Machine</span>{" "}
              Repair Experts
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
              className="text-base sm:text-lg text-slate-500 leading-relaxed mb-6 max-w-lg font-['Poppins']">
              Same-day doorstep service for <strong className="text-[#0F172A]">LG, Samsung, IFB, Bosch, Whirlpool</strong> & more. Expert repair with 90-day warranty.
            </motion.p>

            {/* Trust badges */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
              className="grid grid-cols-2 gap-3 mb-8">
              {[
                { icon: Timer, text: "30-Min Response" },
                { icon: Package, text: "Genuine Parts" },
                { icon: ShieldCheck, text: "90-Day Warranty" },
                { icon: Medal, text: "15,000+ Repairs" },
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-2 bg-white rounded-xl px-3 py-2.5 border border-slate-100 shadow-sm">
                  <b.icon size={18} weight="fill" className="text-[#FF6B00] shrink-0" />
                  <span className="text-xs font-semibold text-[#0F172A] font-['Poppins']">{b.text}</span>
                </div>
              ))}
            </motion.div>

            {/* CTAs */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-3">
              <a href="#book" data-testid="wm-hero-book"
                className="bg-[#FF6B00] text-white hover:bg-[#E55F00] font-bold text-base px-8 py-4 rounded-2xl shadow-xl shadow-orange-200 transition-all flex items-center justify-center gap-2 font-['Poppins']">
                <ArrowRight size={20} weight="bold" /> Book a Technician Now
              </a>
              <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="wm-hero-wa"
                className="bg-[#22C55E] text-white hover:bg-[#16A34A] font-bold text-base px-8 py-4 rounded-2xl shadow-xl shadow-green-200 transition-all flex items-center justify-center gap-2 font-['Poppins']">
                <WhatsappLogo size={20} weight="fill" /> WhatsApp Booking
              </a>
            </motion.div>
          </div>

          {/* Right: Booking Form */}
          <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.3 }}>
            <HeroForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── BRANDS ───
function BrandsSection() {
  return (
    <section data-testid="wm-brands" className="bg-white py-10 border-y border-slate-100">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <p className="text-center text-xs font-bold tracking-[0.2em] uppercase text-slate-400 mb-6 font-['Poppins']">Brands We Service</p>
        <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
          {BRANDS.map((b, i) => (
            <Fade key={b} delay={i * 0.05}>
              <div className="bg-slate-50 border border-slate-100 rounded-2xl px-6 py-3 text-sm font-bold text-[#0F172A] font-['Poppins'] hover:border-[#FF6B00]/30 hover:shadow-md transition-all">
                {b}
              </div>
            </Fade>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── PROBLEMS ───
function ProblemsSection() {
  return (
    <section data-testid="wm-problems" className="bg-[#F8FAFC] py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Fade>
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF6B00] mb-3 block font-['Poppins']">Common Issues</span>
            <h2 className="font-['Poppins'] text-3xl sm:text-4xl font-extrabold text-[#0F172A] tracking-tight">Problems We Fix</h2>
          </div>
        </Fade>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {PROBLEMS.map((p, i) => (
            <Fade key={i} delay={i * 0.05}>
              <div data-testid={`wm-problem-${i}`}
                className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all group text-center">
                <div className="w-12 h-12 bg-[#FF6B00]/10 rounded-2xl flex items-center justify-center mx-auto mb-3 group-hover:bg-[#FF6B00]/20 transition-colors">
                  <p.icon size={24} weight="duotone" className="text-[#FF6B00]" />
                </div>
                <h4 className="font-['Poppins'] text-sm font-bold text-[#0F172A] mb-1">{p.title}</h4>
                <p className="text-xs text-slate-400 font-['Poppins'] leading-relaxed">{p.desc}</p>
              </div>
            </Fade>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── PRICING ───
function PricingSection() {
  return (
    <section data-testid="wm-pricing" className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Fade>
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF6B00] mb-3 block font-['Poppins']">Transparent Pricing</span>
            <h2 className="font-['Poppins'] text-3xl sm:text-4xl font-extrabold text-[#0F172A] tracking-tight mb-3">No Hidden Charges</h2>
            <p className="text-slate-400 font-['Poppins'] text-sm max-w-md mx-auto">What you see is what you pay. Final pricing may vary based on brand and spare parts needed.</p>
          </div>
        </Fade>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
          {PRICING.map((p, i) => (
            <Fade key={i} delay={i * 0.08}>
              <div data-testid={`wm-price-${i}`}
                className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all group relative overflow-hidden">
                <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${p.color} opacity-10 rounded-bl-[40px]`} />
                <div className={`w-11 h-11 bg-gradient-to-br ${p.color} rounded-2xl flex items-center justify-center mb-4 shadow-lg`}>
                  <p.icon size={22} weight="fill" className="text-white" />
                </div>
                <h4 className="font-['Poppins'] text-sm font-semibold text-slate-500 mb-2">{p.service}</h4>
                <div className="flex items-baseline gap-1">
                  <span className="font-['Poppins'] text-3xl font-extrabold text-[#0F172A]">₹{p.price}</span>
                  <span className="text-xs text-slate-400 font-['Poppins']">onwards</span>
                </div>
              </div>
            </Fade>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── WHY CHOOSE US ───
function WhyUsSection() {
  return (
    <section data-testid="wm-whyus" className="bg-[#0F172A] py-16 md:py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#FF6B00]/10 via-transparent to-transparent" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
        <Fade>
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF6B00] mb-3 block font-['Poppins']">Why Choose Us</span>
            <h2 className="font-['Poppins'] text-3xl sm:text-4xl font-extrabold text-white tracking-tight">The Home Tech Difference</h2>
          </div>
        </Fade>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6">
          {WHY_US.map((w, i) => (
            <Fade key={i} delay={i * 0.06}>
              <div data-testid={`wm-why-${i}`}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 hover:bg-white/10 hover:border-[#FF6B00]/30 transition-all">
                <w.icon size={28} weight="duotone" className="text-[#FF6B00] mb-3" />
                <h4 className="font-['Poppins'] text-sm font-bold text-white mb-1">{w.title}</h4>
                <p className="text-xs text-white/50 font-['Poppins'] leading-relaxed">{w.desc}</p>
              </div>
            </Fade>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── REVIEWS ───
function ReviewsSection() {
  return (
    <section data-testid="wm-reviews" className="bg-[#F8FAFC] py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Fade>
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF6B00] mb-3 block font-['Poppins']">Customer Reviews</span>
            <h2 className="font-['Poppins'] text-3xl sm:text-4xl font-extrabold text-[#0F172A] tracking-tight">What Bangalore Says</h2>
          </div>
        </Fade>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {REVIEWS.map((r, i) => (
            <Fade key={i} delay={i * 0.1}>
              <div data-testid={`wm-review-${i}`}
                className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-lg transition-all">
                <div className="flex gap-0.5 mb-3">
                  {[...Array(r.rating)].map((_, j) => <Star key={j} size={16} weight="fill" className="text-amber-400" />)}
                </div>
                <p className="text-sm text-slate-600 font-['Poppins'] leading-relaxed mb-5">"{r.text}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#FF6B00] to-[#FF8C00] rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {r.name.split(" ").map(n => n[0]).join("")}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#0F172A] font-['Poppins']">{r.name}</p>
                    <p className="text-xs text-slate-400 flex items-center gap-1"><MapPin size={10} weight="fill" /> {r.area}</p>
                  </div>
                  <SealCheck size={20} weight="fill" className="text-[#22C55E] ml-auto" />
                </div>
              </div>
            </Fade>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── BOOKING FORM ───
function BookingSection() {
  const [form, setForm] = useState({ name: "", phone: "", area: "", service: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const brands = ["LG", "Samsung", "IFB", "Bosch", "Whirlpool", "Haier", "Panasonic", "Other"];
  const problems = ["Not Spinning", "Water Leakage", "Drainage Issue", "PCB Issue", "Motor Failure", "Drum Noise", "Power Problem", "Door Lock", "Other"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.area || !form.service) { toast.error("Please fill all fields"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, { ...form, gclid: getGclid(), source: getSource() });
      if (res.data.success) { navigate(`/thank-you?name=${encodeURIComponent(form.name)}&service=washing-machine`); }
    } catch { toast.error("Something went wrong. Please try again."); }
    setLoading(false);
  };

  return (
    <section id="book" data-testid="wm-booking" className="bg-white py-16 md:py-24">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <Fade>
          <div className="text-center mb-10">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF6B00] mb-3 block font-['Poppins']">Quick Booking</span>
            <h2 className="font-['Poppins'] text-3xl sm:text-4xl font-extrabold text-[#0F172A] tracking-tight mb-2">Book a Technician in 30 Seconds</h2>
          </div>
        </Fade>
          <Fade>
            <form onSubmit={handleSubmit} data-testid="wm-booking-form" className="bg-[#F8FAFC] rounded-3xl p-6 sm:p-10 border border-slate-100">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 font-['Poppins']">Name</label>
                  <input data-testid="wm-form-name" type="text" placeholder="Your Name" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-white border border-slate-200 rounded-xl p-3.5 text-sm font-['Poppins'] focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/10 outline-none transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 font-['Poppins']">Phone</label>
                  <input data-testid="wm-form-phone" type="tel" placeholder="Mobile Number" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full bg-white border border-slate-200 rounded-xl p-3.5 text-sm font-['Poppins'] focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/10 outline-none transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 font-['Poppins']">Area</label>
                  <input data-testid="wm-form-area" type="text" placeholder="Your Area in Bangalore" value={form.area}
                    onChange={(e) => setForm({ ...form, area: e.target.value })}
                    className="w-full bg-white border border-slate-200 rounded-xl p-3.5 text-sm font-['Poppins'] focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/10 outline-none transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 font-['Poppins']">Problem Type</label>
                  <Select value={form.service} onValueChange={(val) => setForm({ ...form, service: val })}>
                    <SelectTrigger data-testid="wm-form-service" className="w-full bg-white border border-slate-200 rounded-xl p-3.5 h-auto text-sm font-['Poppins'] focus:ring-2 focus:ring-[#FF6B00]/10">
                      <SelectValue placeholder="Select Problem" />
                    </SelectTrigger>
                    <SelectContent>
                      {problems.map(p => <SelectItem key={p} value={p.toLowerCase().replace(/\s/g, "-")}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <button type="submit" disabled={loading} data-testid="wm-booking-submit"
                className="w-full mt-6 bg-[#FF6B00] text-white hover:bg-[#E55F00] font-bold text-base px-8 py-4 rounded-2xl shadow-xl shadow-orange-200 transition-all flex items-center justify-center gap-2 font-['Poppins'] disabled:opacity-50">
                {loading ? "Booking..." : "Book Technician Now"} {!loading && <ArrowRight size={18} weight="bold" />}
              </button>
              <div className="text-center mt-4">
                <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="wm-booking-wa"
                  className="text-[#22C55E] font-semibold text-sm font-['Poppins'] inline-flex items-center gap-1.5 hover:underline">
                  <WhatsappLogo size={16} weight="fill" /> Or book via WhatsApp
                </a>
              </div>
            </form>
          </Fade>
      </div>
    </section>
  );
}

// ─── SERVICE AREAS ───
function AreasSection() {
  return (
    <section data-testid="wm-areas" className="bg-[#F8FAFC] py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Fade>
          <div className="text-center mb-10">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF6B00] mb-3 block font-['Poppins']">Coverage</span>
            <h2 className="font-['Poppins'] text-3xl sm:text-4xl font-extrabold text-[#0F172A] tracking-tight">We Cover All Bangalore</h2>
          </div>
        </Fade>
        <Fade delay={0.1}>
          <div className="flex flex-wrap justify-center gap-2.5 max-w-3xl mx-auto">
            {AREAS.map((a, i) => (
              <span key={i} className="bg-white text-slate-600 text-sm font-medium font-['Poppins'] px-5 py-2.5 rounded-full border border-slate-100 shadow-sm hover:border-[#FF6B00]/30 hover:text-[#FF6B00] hover:shadow-md transition-all cursor-default">
                {a}
              </span>
            ))}
          </div>
        </Fade>
      </div>
    </section>
  );
}

// ─── FAQ ───
function FAQSection() {
  const [open, setOpen] = useState(null);
  return (
    <section data-testid="wm-faq" className="bg-white py-16 md:py-24">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <Fade>
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF6B00] mb-3 block font-['Poppins']">FAQ</span>
            <h2 className="font-['Poppins'] text-3xl sm:text-4xl font-extrabold text-[#0F172A] tracking-tight">Got Questions?</h2>
          </div>
        </Fade>
        <div className="space-y-3">
          {FAQS.map((faq, i) => (
            <Fade key={i} delay={i * 0.05}>
              <div data-testid={`wm-faq-${i}`}
                className="bg-[#F8FAFC] border border-slate-100 rounded-2xl overflow-hidden transition-all">
                <button onClick={() => setOpen(open === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left">
                  <span className="font-['Poppins'] text-sm font-semibold text-[#0F172A] pr-4">{faq.q}</span>
                  <CaretDown size={18} weight="bold" className={`text-[#FF6B00] shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {open === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}>
                      <p className="px-5 pb-5 text-sm text-slate-500 font-['Poppins'] leading-relaxed">{faq.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Fade>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FINAL CTA ───
function FinalCTA() {
  return (
    <section data-testid="wm-final-cta" className="bg-[#0F172A] py-16 md:py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial_gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#FF6B00]/15 via-transparent to-transparent" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
        <Fade>
          <h2 className="font-['Poppins'] text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-3 leading-tight">
            Don't Let a Broken Machine{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B00] to-[#FF8C00]">Ruin Your Day.</span>
          </h2>
          <p className="text-white/50 font-['Poppins'] text-base mb-8 max-w-md mx-auto">Expert repair at your doorstep. Starting ₹350. 90-day warranty.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#book" data-testid="wm-final-book"
              className="bg-[#FF6B00] text-white hover:bg-[#E55F00] font-bold text-lg px-10 py-5 rounded-2xl shadow-xl shadow-orange-900/20 transition-all inline-flex items-center justify-center gap-2 font-['Poppins']">
              <ArrowRight size={22} weight="bold" /> Book a Technician Now
            </a>
            <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="wm-final-wa"
              className="bg-[#22C55E] text-white hover:bg-[#16A34A] font-bold text-lg px-10 py-5 rounded-2xl shadow-xl transition-all inline-flex items-center justify-center gap-2 font-['Poppins']">
              <WhatsappLogo size={22} weight="fill" /> WhatsApp Us
            </a>
          </div>
        </Fade>
      </div>
    </section>
  );
}

// ─── FOOTER ───
function WMFooter() {
  return (
    <footer data-testid="wm-footer" className="bg-[#0F172A] border-t border-white/5 py-8">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-gradient-to-br from-[#FF6B00] to-[#FF8C00] rounded-lg flex items-center justify-center">
              <Wrench size={14} weight="bold" className="text-white" />
            </div>
            <div>
              <span className="font-['Poppins'] font-bold text-white text-sm block">Home Tech Repair</span>
              <span className="text-white/30 text-xs font-['Poppins']">Washing Machine Repair — Bangalore</span>
            </div>
          </div>
          <div className="flex items-center gap-5 text-sm text-white/40 font-['Poppins']">
            <a href={WA} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors flex items-center gap-1.5"><WhatsappLogo size={14} weight="fill" /> WhatsApp</a>
            <a href="#book" className="hover:text-white transition-colors flex items-center gap-1.5"><ArrowRight size={14} weight="bold" /> Book Online</a>
          </div>
        </div>
        <div className="mt-6 pt-6 border-t border-white/5 text-center text-xs text-white/20 font-['Poppins']">
          Home Tech Repair Bangalore. Washing machine repair for LG, Samsung, IFB, Bosch, Whirlpool.
        </div>
      </div>
    </footer>
  );
}

// ─── STICKY MOBILE CTA ───
function StickyMobile() {
  return (
    <div data-testid="wm-sticky-cta" className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white/90 backdrop-blur-xl border-t border-slate-100 p-2 flex gap-2">
      <a href="#book" className="flex-1 bg-[#FF6B00] text-white font-bold text-sm py-3.5 rounded-xl flex items-center justify-center gap-1.5 shadow-lg shadow-orange-200 font-['Poppins']">
        <ArrowRight size={16} weight="bold" /> Book Now
      </a>
      <a href={WA} target="_blank" rel="noopener noreferrer" className="flex-1 bg-[#22C55E] text-white font-bold text-sm py-3.5 rounded-xl flex items-center justify-center gap-1.5 shadow-lg shadow-green-200 font-['Poppins']">
        <WhatsappLogo size={16} weight="fill" /> WhatsApp
      </a>
    </div>
  );
}

// ─── MAIN ───
export default function WashingMachinePage() {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <WMHeader />
      <WMHero />
      <BrandsSection />
      <ProblemsSection />
      <PricingSection />
      <WhyUsSection />
      <ReviewsSection />
      <BookingSection />
      <AreasSection />
      <FAQSection />
      <FinalCTA />
      <WMFooter />
      <StickyMobile />
    </div>
  );
}
